import { matchProducts } from '../api/products';

// GS1 reserves EAN-13 prefixes 20-29 for "restricted circulation" numbers, i.e.
// codes assigned in-store instead of by a manufacturer. Generated codes stay in
// that range so they can never clash with a real product's GTIN.
const IN_STORE_PREFIX = '2';

const MAX_UNIQUENESS_ATTEMPTS = 3;

/** EAN-13 check digit for the first 12 digits (odd positions x1, even x3). */
export const ean13CheckDigit = (twelveDigits) => {
  const sum = String(twelveDigits)
    .split('')
    .reduce((acc, digit, i) => acc + Number(digit) * (i % 2 === 0 ? 1 : 3), 0);
  return String((10 - (sum % 10)) % 10);
};

/** A random, structurally valid EAN-13 (12 digits + check digit). */
export const generateEan13 = () => {
  let body = IN_STORE_PREFIX;
  while (body.length < 12) {
    body += Math.floor(Math.random() * 10);
  }
  return body + ean13CheckDigit(body);
};

/**
 * Indexes of line items that will create a NEW item (nothing linked) but carry
 * no barcode. Lines linked to an existing item are ignored: their barcodes
 * belong to that product and are not changed by the create/import flows.
 */
export const findLinesMissingBarcode = (items) =>
  items.reduce((indexes, item, idx) => {
    if (!item.selectedItem && !(item.barcodes?.length > 0)) indexes.push(idx);
    return indexes;
  }, []);

/** Human label for a line in the missing-barcode warning. */
export const describeLine = (item, idx) =>
  `Item ${idx + 1}${item.name || item.sku ? ` — ${item.name || item.sku}` : ''}`;

/** Barcodes among `codes` that a product of this business already uses. */
const findTakenBarcodes = async (codes, businessUuid) => {
  const matches = await matchProducts([], codes, [], businessUuid);
  const taken = new Set();
  (Array.isArray(matches) ? matches : []).forEach((match) =>
    (match.barcodes || []).forEach((barcode) => taken.add(String(barcode))),
  );
  return taken;
};

/**
 * Generates `count` EAN-13 codes that are unique within the batch and not
 * already used by a product of this business.
 *
 * Throws if the uniqueness lookup fails, so callers surface the problem instead
 * of assigning a barcode that may already exist.
 */
export const generateUniqueBarcodes = async (count, businessUuid) => {
  if (count <= 0) return [];

  let codes = [];
  while (codes.length < count) {
    const code = generateEan13();
    if (!codes.includes(code)) codes.push(code);
  }

  for (let attempt = 0; attempt < MAX_UNIQUENESS_ATTEMPTS; attempt += 1) {
    const taken = await findTakenBarcodes(codes, businessUuid);
    if (taken.size === 0) break;
    codes = codes.map((code) => (taken.has(code) ? generateEan13() : code));
  }

  return codes;
};
